import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";

import {
  attachStageManualViewListeners,
  type LockedWheelZoomHandler,
} from "@/lib/timeline/view/StageManualView/view";
import type { TimelineViewTransform } from "@/lib/timeline/view/ViewTransform/transform";

export type { TimelineViewTransform } from "@/lib/timeline/view/ViewTransform/transform";

interface DragSession {
  view: TimelineViewTransform;
  pointerX: number;
  pointerY: number;
}

export interface TimelineZoomState extends TimelineViewTransform {
  resetToHome: () => void;
  resetToHomeKeepingZoom: () => void;
  setViewTransform: (view: TimelineViewTransform) => void;
}

export function useTimelineZoom(
  stageRef: RefObject<HTMLElement | null>,
  cameraLockEnabled: boolean,
  getHomeView: (zoom?: number) => TimelineViewTransform | null,
  restoredView: TimelineViewTransform | null = null,
  lockedWheelZoomRef?: RefObject<LockedWheelZoomHandler | null>,
  getGestureView?: () => TimelineViewTransform,
  onPanWhileLocked?: (next: TimelineViewTransform) => void,
  onManualZoom?: () => void,
  onViewChange?: (next: TimelineViewTransform) => void,
): TimelineZoomState {
  const [view, setViewState] = useState<TimelineViewTransform>(
    () => restoredView ?? { zoom: 1, panX: 0, panY: 0 },
  );
  const viewRef = useRef(view);
  viewRef.current = view;
  const dragRef = useRef<DragSession | null>(null);
  const homeSourceRef = useRef(getHomeView);
  homeSourceRef.current = getHomeView;

  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;
  const setViewTransform = useCallback((next: TimelineViewTransform): void => {
    viewRef.current = next;
    onViewChangeRef.current?.(next);
    setViewState(next);
  }, []);

  const resetToHome = useCallback((): void => {
    const home = homeSourceRef.current();
    if (home !== null) {
      setViewTransform(home);
    }
  }, [setViewTransform]);

  const resetToHomeKeepingZoom = useCallback((): void => {
    const home = homeSourceRef.current(viewRef.current.zoom);
    if (home !== null) {
      setViewTransform(home);
    }
  }, [setViewTransform]);

  const cameraLockRef = useRef(cameraLockEnabled);
  cameraLockRef.current = cameraLockEnabled;
  const gestureViewRef = useRef(getGestureView);
  gestureViewRef.current = getGestureView;
  const lockedPanRef = useRef(onPanWhileLocked);
  lockedPanRef.current = onPanWhileLocked;
  const manualZoomRef = useRef(onManualZoom);
  manualZoomRef.current = onManualZoom;
  const lockedWheelRef = useRef(lockedWheelZoomRef);
  lockedWheelRef.current = lockedWheelZoomRef;

  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null) {
      return;
    }
    return attachStageManualViewListeners(stage, {
      allowWheelZoom: true,
      getAllowPanDrag: () => true,
      getLockedPan: () => true,
      getCameraLock: () => cameraLockRef.current,
      onLockedPan: (next) => {
        const unlock = lockedPanRef.current;
        if (unlock !== undefined) {
          unlock(next);
          return;
        }
        setViewTransform(next);
      },
      onManualZoom: () => {
        manualZoomRef.current?.();
      },
      onLockedWheelZoom: (factor, pointerX, pointerY, stageW, stageH) => {
        if (!cameraLockRef.current) {
          return;
        }
        lockedWheelRef.current?.current?.(
          factor,
          pointerX,
          pointerY,
          stageW,
          stageH,
        );
      },
      getView: () => gestureViewRef.current?.() ?? viewRef.current,
      setView: setViewTransform,
      dragRef,
    });
  }, [setViewTransform, stageRef]);

  return { ...view, resetToHome, resetToHomeKeepingZoom, setViewTransform };
}
